import React, { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { fetchDocumentScanThumbnail, fetchDocumentThumbnail } from '../../api/caseDocuments';

interface DocumentThumbnailProps {
  caseId: string;
  documentId: string;
  fileName: string;
  user: User | null;
  onOpen: () => void;
  // 'scan' previews the cleaned page document-scan-service derived from the upload.
  variant?: 'original' | 'scan';
}

type Preview =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'unavailable' };

// Preview of a client upload, so a lawyer can check a passport scan at a
// glance instead of opening every file. Clicking it opens the document, like
// the row's View button. Uploads the backend cannot render (corrupt files,
// unsupported formats) fall back to the plain file icon.
const DocumentThumbnail: React.FC<DocumentThumbnailProps> = ({ caseId, documentId, fileName, user, onOpen, variant = 'original' }) => {
  const [preview, setPreview] = useState<Preview>({ status: 'loading' });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setPreview({ status: 'loading' });

    user
      .getIdToken()
      .then(token => (variant === 'scan' ? fetchDocumentScanThumbnail : fetchDocumentThumbnail)(token, caseId, documentId))
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreview({ status: 'ready', url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setPreview({ status: 'unavailable' });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [user, caseId, documentId, variant]);

  return (
    <button
      type="button"
      className={`document-thumbnail document-thumbnail-${preview.status}`}
      onClick={onOpen}
      aria-label={`Open ${fileName}`}
      aria-busy={preview.status === 'loading'}
      title={`Open ${fileName}`}
    >
      {preview.status === 'ready' && <img src={preview.url} alt={`Preview of ${fileName}`} />}
      {preview.status === 'unavailable' && (
        <span className="document-thumbnail-fallback" aria-hidden="true">📄</span>
      )}
    </button>
  );
};

export default DocumentThumbnail;
